import React, { useState } from 'react';
import {
  Folder,
  FolderOpen,
  Calendar,
  FileText,
  Box,
  MapPin,
  ChevronRight,
  Home,
} from 'lucide-react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import AppConfiguration from './components/AppConfiguration';
import Category from './components/Category';
import SubcategoryItem from './components/SubcategoryItem';
import Slot from './components/Slot';
import Centers from '../center-management';
import DiagnosticCenter from './components/DiagnosticCenter';
import City from './components/City';
import ESignatureManagement from './components/ESignatureManagement';
import ReportLayoutPage from './components/ReportLayoutPage';
import PincodeManagement from './components/PincodeManagement';
import Sources from './components/Sources';
import CollectionPriceManagement from './components/CollectionPriceManagement';
import Department from './components/Department';

const MasterManagement = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('categories');

  const menuItems = [
    {
      id: 'department',
      label: 'Department',
      icon: Folder,
      component: Department,
      description: 'Manage product departments'
    },
    {
      id: 'categories',
      label: 'Categories',
      icon: Folder,
      component: Category,
      description: 'Manage product categories'
    },
    {
      id: 'subcategories',
      label: 'Subcategories',
      icon: FolderOpen,
      component: SubcategoryItem,
      description: 'Manage product subcategories'
    },
    {
      id: 'slots',
      label: 'Home Sample Slots',
      icon: Calendar,
      component: Slot,
      description: 'Manage appointment slots'
    },
    {
      id: 'pincodes',
      label: 'Pincodes',
      icon: MapPin, // Updated with a location-based icon
      component: PincodeManagement,
      description: 'Manage services pincodes'
    },
    {
      id: 'city',
      label: 'Cities',
      icon: MapPin, // Updated to a location-based icon
      component: City,
      description: 'Manage cities'
    },
    {
      id: 'diagnosticcenter',
      label: 'Venue',
      icon: FileText,
      component: DiagnosticCenter,
      description: 'Manage diagnostic centers / main hospitals'
    },
    {
      id: 'esignature',
      label: 'E-Signature',
      icon: FileText,
      component: ESignatureManagement,
      description: 'Manage Signature'
    },
    {
      id: 'reportlayout',
      label: 'Report Layout',
      icon: FileText,
      component: ReportLayoutPage,
      description: 'Configure header, footer, and advertisement images for your report layout'
    },
   
    {
      id: 'source',
      label: 'Source',
      icon: Box, // More fitting for a "Source" category
      component: Sources,
      description: 'Sources'
    },
     {
      id: 'collectionPrice',
      label: 'Collection Price',
      icon: Box, 
      component: CollectionPriceManagement,
      description: 'Collection Price'
    }
  ];

  const ActiveComponent = menuItems.find(item => item.id === activeTab)?.component;

  const handleMenuToggle = () => setSidebarOpen(!sidebarOpen);
  const handleSidebarClose = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuToggle={handleMenuToggle} isMenuOpen={sidebarOpen} />
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main
        className={`pt-16 transition-all duration-300 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-4">
          <Breadcrumb />

          {/* Enhanced Header */}
          <div className="mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Master Data Management
                </h1>
                <p className="text-xs text-gray-600 mt-1">
                  Manage categories, coupons, templates, and system configuration
                </p>
              </div>

              <div className="flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-gray-600">Active</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  <span className="text-gray-600">Inactive</span>
                </div>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Master Data Sections
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {menuItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = activeTab === item.id;

                      return (
                        <button
                          key={item.id}
                          onClick={() => setActiveTab(item.id)}
                          className={`flex items-center space-x-2 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                            isActive
                              ? 'bg-blue-50 text-blue-700 border border-blue-200'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 border border-gray-200'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          <span>{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Active Component */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    {menuItems.find(item => item.id === activeTab)?.label}
                  </h3>
                  <p className="text-xs text-gray-600 mt-0.5">
                    {menuItems.find(item => item.id === activeTab)?.description}
                  </p>
                </div>
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <Home className="w-3 h-3" />
                  <ChevronRight className="w-3 h-3" />
                  <span>Master Management</span>
                  <ChevronRight className="w-3 h-3" />
                  <span className="text-gray-700 font-medium">
                    {menuItems.find(item => item.id === activeTab)?.label}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-4">
              {ActiveComponent && <ActiveComponent />}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MasterManagement;
